import React, { useState, useEffect, useRef, forwardRef } from 'react';
import ValidationError from '../ValidationError/ValidationError';
import css from './RichTextEditor.module.css';

// Only import ReactQuill on client side
let ReactQuill = null;
if (typeof window !== 'undefined') {
  ReactQuill = require('react-quill');
  require('react-quill/dist/quill.snow.css');
}

const RichTextEditor = forwardRef((props, ref) => {
  const {
    value,
    onChange,
    onBlur,
    onFocus,
    hideErrorMessage,
    meta,
    customErrorText
  } = props;

  const isControlled = value !== undefined;
  const [localValue, setLocalValue] = useState(isControlled ? '' : '');

  const [isMounted, setIsMounted] = useState(false);
  const quillRef = useRef(null);
  const quillInstanceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Set mounted state to true when component mounts on client
    setIsMounted(true);

    // When switching from uncontrolled to controlled, clear local state usage
    // No-op for controlled mode; Quill will use value prop directly
    return () => {
      setIsMounted(false);
    };
  }, [isControlled]);

  // Suppress findDOMNode warnings specifically for react-quill
  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('findDOMNode is deprecated')) {
        return; // Suppress the findDOMNode warning
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      console.error = originalConsoleError;
    };
  }, []);

  // Don't render Quill on server or if ReactQuill is not loaded
  if (!isMounted || !ReactQuill) {
    return <div className={css.richTextEditor} />;
  }

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }, { 'header': 'paragraph' }],
      ['bold', 'italic', 'underline'],
      [{ 'align': [] }],
      // [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'align',
    // 'list', 'bullet', 'indent',
  ];

  const { valid, invalid, touched, error } = meta;

  const errorText = customErrorText || error;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!customErrorText || !!(touched && invalid && error);
  const fieldMeta = { touched: hasError, error: errorText };

  // add styles here
  const styles = {
    height: '200px',
    borderRadius: '4px',
    overflow: 'hidden'
  };

  if (hasError) {
    Object.assign(styles, {
      border: '1px solid red'
    });
  }

  const setQuillRef = (element) => {
    if (element) {
      quillRef.current = element;
      // Try to get the editor instance without triggering findDOMNode
      try {
        if (element.getEditor) {
          quillInstanceRef.current = element.getEditor();
        }
      } catch (e) {
        // Silently handle any errors to avoid console warnings
      }
      
      if (ref) {
        if (typeof ref === 'function') {
          ref(element);
        } else {
          ref.current = element;
        }
      }
    }
  };

  return (
    <div ref={containerRef}>
      <ReactQuill
        key={'quill-rich-editor'}
        ref={setQuillRef}
        theme="snow"
        value={isControlled ? value : localValue}
        onChange={(content, delta, source, editor) => {
          if (isControlled) {
            if (onChange) {
              if (content === '<p><br></p>') {
                onChange(undefined);
              } else {
                onChange(content);
              }
            };
          } else {
            setLocalValue(content);
            if (onChange) {
              if (content === '<p><br></p>') {
                onChange(undefined);
              } else {
                onChange(content);
              }
            };
          }
        }}
        onBlur={onBlur}
        onFocus={onFocus}
        modules={modules}
        formats={formats}
        style={styles}
        className={css.quillEditor}
        placeholder=""
      />
      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;